import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, X, Calculator, DollarSign } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

const analysisData = [
  {
    category: "Total Monthly Cost",
    eAssist: "3.5% + $1,775",
    wisdom: "3.5% + $2,494",
    limitations:
      "Significantly more expensive. Practices typically overpay by ~$2,000/mo due to layered fees.",
    isTotal: true,
  },
  {
    category: "Insurance Billing",
    eAssist: "3.5% of Collections",
    wisdom: "3.5% of Collections",
    limitations:
      "Relies on manual labor which is slower, error-prone, and lacks real-time transparency.",
    isPrice: true,
  },
  {
    category: "AI RCM Suite",
    eAssist: "Not Included",
    wisdom: "Not Included",
    limitations:
      "Zero automation. You pay premium rates for humans to do work that AI could do instantly.",
    isPositive: true,
  },
  {
    category: "Insurance Verification",
    eAssist: "$8-12/patient",
    wisdom: "$99/mo + $8.95/patient",
    limitations:
      "Charged as an extra fee. Manual verification is slow and often incomplete compared to AI.",
    isNegative: true,
  },
  {
    category: "Patient Billing",
    eAssist: "$925+/mo",
    wisdom: "$1,097-$1,797/mo",
    limitations:
      "Charged as an extra fee. Manual follow-ups are inconsistent and less effective at collecting.",
    isNegative: true,
  },
  {
    category: "Payment Reconciliation",
    eAssist: "$199+/mo",
    wisdom: "Not Included",
    limitations:
      "Charged as an extra fee or not offered. Manual posting lags behind, making reporting inaccurate.",
    isNegative: true,
  },
  {
    category: "Denial Management",
    eAssist: "Often Not Included",
    wisdom: "Included in billing",
    limitations:
      "Many vendors charge extra for appeals or provide limited follow-through.",
    isNegative: true,
  },
];

const renderCell = (value, item) => {
  if (item.isTotal) {
    return (
      <span className="text-base font-extrabold text-slate-900 tracking-tight">
        {value}
      </span>
    );
  }
  if (item.isPrice) {
    return (
      <span className="text-base font-bold text-slate-700 tracking-tight">
        {value}
      </span>
    );
  }
  if (value === "Included" || value === "Included in billing") {
    return (
      <div className="flex items-center gap-2">
        <Check className="w-4 h-4 text-slate-400" />
        <span className="text-base text-slate-600">{value}</span>
      </div>
    );
  }
  if (value === "Not Included") {
    return (
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
          <X className="w-3.5 h-3.5 text-red-400" />
        </div>
        <span className="text-base text-slate-400">{value}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <div className="w-5 h-5 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
        <X className="w-3.5 h-3.5 text-red-400" />
      </div>
      <span className="text-base text-slate-500">{value}</span>
    </div>
  );
};

const calculateCost = (collections, patients) => {
  let billingCost = 0;
  if (collections <= 100000) {
    billingCost = collections * 0.035;
  } else if (collections <= 150000) {
    billingCost = 100000 * 0.035 + (collections - 100000) * 0.03;
  } else {
    billingCost =
      100000 * 0.035 + 50000 * 0.03 + (collections - 150000) * 0.025;
  }

  const insuranceVerif = patients * 8;
  const patientBilling = 1000;
  const paymentRec = 200;

  return {
    billing: Math.round(billingCost),
    insuranceVerif,
    patientBilling,
    paymentRec,
    total:
      Math.round(billingCost) + insuranceVerif + patientBilling + paymentRec,
  };
};

const RCMPricingComparisonSlide = () => {
  const [collections, setCollections] = useState(150000);
  const [patients, setPatients] = useState(400);
  const cost = calculateCost(collections, patients);

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 lg:p-8 relative overflow-hidden font-sans">
      {/* Background */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100 rounded-full blur-[120px] opacity-40 translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-red-50 rounded-full blur-[100px] opacity-40 -translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 flex flex-col h-full justify-center">
        {/* Header */}
        <div className="mb-5 flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-2 border border-blue-100">
              <DollarSign className="w-4 h-4" />
              <span>RCM Pricing</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              What do other <span className="text-blue-600">RCM vendors</span> charge?
            </h2>
          </motion.div>
        </div>

        {/* Main Content */}
        <div className="flex gap-5 items-stretch">
          {/* Left: Comparison Table */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="flex-1 bg-white rounded-3xl border border-slate-200/60 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] overflow-hidden"
          >
            <Table>
              <TableHeader className="bg-slate-50 sticky top-0">
                <TableRow className="border-b border-slate-200/80">
                  <TableHead className="text-sm uppercase tracking-widest font-bold text-slate-400 py-4 px-6 w-[35%]">
                    Service
                  </TableHead>
                  <TableHead className="text-sm uppercase tracking-widest font-bold text-slate-400 py-4 px-6 w-[32.5%]">
                    eAssist
                  </TableHead>
                  <TableHead className="text-sm uppercase tracking-widest font-bold text-slate-400 py-4 px-6 w-[32.5%]">
                    Wisdom
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analysisData.map((item, index) => (
                  <TableRow
                    key={index}
                    className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${
                      item.isTotal
                        ? "bg-gradient-to-r from-transparent via-blue-50/30 to-transparent shadow-sm"
                        : ""
                    }`}
                  >
                    <TableCell
                      className={`py-3.5 px-6 ${item.isTotal ? "text-lg font-extrabold text-slate-900 tracking-wide" : "text-base font-semibold text-slate-700"}`}
                    >
                      {item.category}
                    </TableCell>
                    <TableCell className="py-3.5 px-6">
                      {renderCell(item.eAssist, item)}
                    </TableCell>
                    <TableCell className="py-3.5 px-6">
                      {renderCell(item.wisdom, item)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </motion.div>

          {/* Right: Calculator */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="w-[370px] bg-[#0f172a] rounded-2xl shadow-xl p-5 flex flex-col text-white"
          >
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-xl bg-blue-500/20 ring-1 ring-blue-400/30 flex items-center justify-center">
                <Calculator className="w-4.5 h-4.5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">
                  Cost Calculator
                </h3>
                <p className="text-[10px] text-slate-400">
                  See what eAssist/Wisdom would charge
                </p>
              </div>
            </div>

            {/* Inputs */}
            <div className="space-y-4 mb-4">
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="text-xs font-semibold text-slate-300">
                    Monthly Collections
                  </label>
                  <span className="text-sm font-bold text-white">
                    ${collections.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="50000"
                  max="500000"
                  step="10000"
                  value={collections}
                  onChange={(e) => setCollections(Number(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:shadow-md"
                />
                <div className="flex justify-between text-[10px] text-slate-500 mt-0.5">
                  <span>$50K</span>
                  <span>$500K</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="text-xs font-semibold text-slate-300">
                    Patients / Month
                  </label>
                  <span className="text-sm font-bold text-white">
                    {patients}
                  </span>
                </div>
                <input
                  type="range"
                  min="100"
                  max="1000"
                  step="25"
                  value={patients}
                  onChange={(e) => setPatients(Number(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:shadow-md"
                />
                <div className="flex justify-between text-[10px] text-slate-500 mt-0.5">
                  <span>100</span>
                  <span>1,000</span>
                </div>
              </div>
            </div>

            {/* Breakdown */}
            <div className="bg-slate-800/50 rounded-xl ring-1 ring-white/10 p-3.5 space-y-2 mb-3 flex-1">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                Monthly Cost Breakdown
              </p>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Insurance Billing</span>
                <span className="font-semibold text-white">
                  ${cost.billing.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">
                  Insurance Verif ($8/pt)
                </span>
                <span className="font-semibold text-white">
                  ${cost.insuranceVerif.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Patient Billing</span>
                <span className="font-semibold text-white">
                  ${cost.patientBilling.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Payment Reconciliation</span>
                <span className="font-semibold text-white">
                  ${cost.paymentRec.toLocaleString()}
                </span>
              </div>
              <div className="h-px bg-white/10 my-1.5" />
              <p className="text-[9px] text-slate-500">
                First $100K at 3.5% | $100-150K at 3% | $150K+ at 2.5%
              </p>
            </div>

            {/* Total */}
            <div className="bg-red-500/10 rounded-xl ring-1 ring-red-400/30 p-3.5 text-center">
              <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider block mb-0.5">
                Their Total Monthly Cost
              </span>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-2xl font-extrabold text-red-400 tracking-tight tabular-nums">
                  ${cost.total.toLocaleString()}
                </span>
                <span className="text-sm text-red-400/60 font-medium">/mo</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RCMPricingComparisonSlide;
